import { Popover } from 'antd';
import React from 'react';

import { Divider } from '@common/components';
import { useMutation, useNavigate, useLocation } from '@common/hooks';
import {
  SignOutAll,
  SignOutAllResponse,
  SignOutAllVariables,
  SignOutMutation,
  SignOutMutationResponse,
} from '@identity/graphql';
import Auth from 'works/legacy/lib/auth';
import Filters from 'works/legacy/lib/filters';
import { createMessage } from 'works/legacy/lib/message-helper';
import { groupBy } from 'lodash';
import { AccountOption } from './_account-option';
import { LinkOption } from './_link-option';
import { AccountLeading } from './_account-leading';
import { redirectToLogin } from 'works/legacy/lib/redirect-to-login';

export const AppHeaderAccountsMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [signOut, signOutStatus] = useMutation<SignOutMutationResponse>(
    SignOutMutation,
    {
      onCompleted() {
        Auth.signOut();
        Filters.clearFilters();
        // Requires a hard refresh so that a new and valid csrf token is fetch
        // from the rails headers.
        redirectToLogin();
      },
      onError: () => {
        createMessage('error', 'Failed to logout your account.');
      },
    }
  );

  const [signOutAllAccounts, signOutAllStatus] = useMutation<
    SignOutAllResponse,
    SignOutAllVariables
  >(SignOutAll, {
    onCompleted: (data) => {
      if (!data.signOutAll.success) {
        return;
      }

      Auth.reset();

      redirectToLogin();
    },
    onError: () => {
      createMessage('error', 'Failed to logout all accounts.');
    },
  });

  const handleLogout = () => {
    if (signOutStatus.loading) {
      return;
    }

    signOut();
  };

  const handleLogoutAll = () => {
    if (signOutAllStatus.loading) {
      return;
    }

    signOutAllAccounts({
      variables: {
        input: Auth.signOutAllData(),
      },
    });
  };

  const handleSwitch = ({ membershipId }: { membershipId: string }) => {
    Auth.switch(membershipId, location.pathname);
    const previousPath = Auth.path();
    const saveLastVisitedPage = Auth.saveLastVisitedPage();

    if (previousPath && saveLastVisitedPage) {
      window.location.href = previousPath;
    } else {
      window.location.href = '/works';
    }
  };

  const handleNewAccount = () => {
    Auth.switch('new-account', location.pathname);

    navigate('/works/sign_in?newSession=true');
  };

  const currentAccount = Auth.account();
  const accountsByEmail = groupBy(
    Auth.allAccounts(),
    (account) => account.email
  );

  return (
    <>
      <Popover
        className="app-header__popover"
        overlayClassName="app-header__popover-overlay"
        content={
          <div className="popover-content accounts-menu">
            {Object.entries(accountsByEmail).map(([email, accounts]) => (
              <React.Fragment key={email}>
                <AccountOption
                  accounts={accounts}
                  handleLogout={handleLogout}
                  handleSwitchAccount={handleSwitch}
                />
                <Divider className="spaced-divider" />
              </React.Fragment>
            ))}
            <LinkOption
              icon={{ kind: 'light', icon: 'plus' }}
              onClick={handleNewAccount}
            >
              Add Account
            </LinkOption>
            <LinkOption
              icon={{ kind: 'light', icon: 'userGear' }}
              onClick={() => navigate('/works/account-settings')}
            >
              Account Settings
            </LinkOption>
            <Divider className="spaced-divider" />
            <LinkOption
              icon={{ kind: 'light', icon: 'rightFromBracket' }}
              onClick={handleLogoutAll}
            >
              Logout All Accounts
            </LinkOption>
          </div>
        }
        placement="bottomRight"
        trigger="hover"
      >
        <div>
          <AccountLeading current={currentAccount} />
        </div>
      </Popover>
      {/* Globals because popover is rendered within a portal */}
      <style jsx>
        {`
          :global(.app-header__popover) {
            padding-right: 28px;
            margin-left: 8px;
            min-width: 50px;
            display: flex;
          }
          :global(.app-header__popover-overlay) {
            padding-right: 24px;
          }
          :global(.app-header__popover-overlay .spaced-divider) {
            margin: 8px 0 !important;
          }
          :global(.app-header__popover-overlay .ant-popover-inner-content) {
            padding: 0;
          }
          // can be removed when we migrate to latest v4
          :global(.app-header__popover-overlay .ant-popover-arrow) {
            display: none;
          }
          :global(.app-header__popover-overlay .popover-content) {
            display: flex;
            flex-direction: column;
            margin-top: -10px;
          }
        `}
      </style>
    </>
  );
};
